import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable, useServerTable } from "@dl/web-core-js/table";
import { Badge } from "@dl/web-core-js/ui";
import { useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  formatNumber,
  REPLENISHMENT_POLICIES,
  STOCKING_SOURCES,
  StockingPointClient,
  type StockingPointDTO,
} from "../../api/manufacturing";
import { NexpimView } from "../../views";
import { policyLabel } from "../purchasing/replenishment/MaterialStory";
import { stockingSourceLabel } from "./PartStockedAtTab";

/**
 * Every stocking point: what is kept where, and how. Filter on a warehouse to
 * see what it keeps (VIZU: its paper and raw boards); a part's own points are
 * edited on its Stocked at tab, which a row opens.
 */
export function StockingPointsPage() {
  const { t } = useTranslation();
  const { http, hasView } = useAuth();
  const navigate = useNavigate();
  const allowed = hasView(NexpimView.MANUFACTURING_STOCK);
  const points = useMemo(() => new StockingPointClient(http), [http]);
  const table = useServerTable<StockingPointDTO>({
    queryKey: ["manufacturing", "stocking-point", "all"],
    fetchPage: (request) => points.find(request),
    fetchMatchingIds: (request) => points.findMatchingIds(request),
    enabled: allowed,
  });

  const cols = useMemo(() => {
    const c = defineColumns<StockingPointDTO>();
    return [
      c.text("warehouse_code", (row) => row.warehouse_code ?? "", { header: t("stocking.column.warehouse", "Warehouse"), filter: "text", priority: 1 }),
      c.text("part_number", (row) => row.part_number ?? "", { headerKey: "column.part.number", priority: 1 }),
      c.text("part_name", (row) => row.part_name ?? "", { headerKey: "column.name", priority: 2 }),
      c.text("bin", (row) => row.bin ?? "", { header: t("stocking.column.bin", "Bin"), filter: "text", priority: 3 }),
      c.text("source", (row) => stockingSourceLabel(row.source, t), {
        header: t("stocking.column.source", "Replenished"),
        filter: "multiselect",
        filterOptions: STOCKING_SOURCES.map((source) => ({ value: source, label: stockingSourceLabel(source, t) })),
        priority: 2,
      }),
      c.text("replenishment_policy", (row) => (row.replenishment_policy ? policyLabel(row.replenishment_policy, t) : t("stocking.as-part", "As the part")), {
        header: t("stocking.column.policy", "Planning"),
        filter: "multiselect",
        filterOptions: REPLENISHMENT_POLICIES.map((policy) => ({ value: policy, label: policyLabel(policy, t) })),
        priority: 3,
      }),
      c.custom(
        "is_consumption_point",
        (row) => (row.is_consumption_point ? 1 : 0),
        (row) =>
          row.is_consumption_point ? (
            <Badge variant="accent" size="sm">
              {t("stocking.consumption", "Used from here")}
            </Badge>
          ) : (
            <span />
          ),
        { header: t("stocking.column.consumption", "Used from here"), filter: "none", priority: 3 },
      ),
      c.text("quantity", (row) => (row.quantity == null ? "0" : formatNumber(row.quantity)), {
        header: t("stocking.column.on-hand", "On hand"),
        filter: "none",
        enableSorting: false,
        priority: 2,
      }),
    ];
  }, [t]);

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("stocking.title", "Stocking points")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">{t("error.access-denied.detail", "Your account does not have permission to view this page.")}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        parent={{ to: "/manufacturing/stock", label: t("navigation.item.stock", "Stock") }}
        title={t("stocking.title", "Stocking points")}
        subtitle={t("stocking.subtitle", "What each warehouse keeps, and how it is replenished there. Filter on a warehouse to see what it keeps.")}
      />
      <DataTable<StockingPointDTO>
        data={table.rows}
        columns={cols}
        getRowId={(row) => String(row.id)}
        stateKey="/manufacturing/stock/points"
        loading={table.loading}
        server={table.mode}
        onRowClick={(row) =>
          void navigate({ to: "/manufacturing/parts/$partId", params: { partId: String(row.part_id) }, search: { tab: "stocked-at" } })
        }
        emptyMessage={t("table.empty", "Nothing to show")}
      />
    </>
  );
}
